import { Progress } from '@/components/ui/progress';
import { formatPercent, formatCurrency } from '@/lib/budget-utils';
import type { BudgetStatus } from '@/types/budget.types';

interface ProgressSegment {
  value: number;
  color: string;
  label: string;
}

interface BudgetProgressBarProps {
  budget?: number;
  actual?: number;
  committed?: number;
  status?: BudgetStatus;
  showLabels?: boolean;
  showPercentage?: boolean;
  height?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'stacked';
  // Aliases for backward compatibility
  value?: number;
  max?: number;
  showLabel?: boolean;
  label?: string;
  // Segments for custom stacked progress bar
  segments?: ProgressSegment[];
  showLegend?: boolean;
}

export function BudgetProgressBar({
  budget,
  actual,
  committed = 0,
  status = 'green',
  showLabels = true,
  showPercentage = true,
  height = 'md',
  variant = 'default',
  value,
  max,
  showLabel,
  label,
  segments,
  showLegend = false,
}: BudgetProgressBarProps) {
  // Use aliases if primary values not provided
  const displayBudget = budget ?? max ?? 100;
  const displayActual = actual ?? value ?? 0;
  const displayShowLabels = showLabels ?? showLabel ?? true;
  const actualPercent = (displayActual / displayBudget) * 100;
  const committedPercent = displayBudget > 0 ? (committed / displayBudget) * 100 : 0;
  const totalPercent = actualPercent + committedPercent;
  const availablePercent = 100 - totalPercent;

  const getBarColor = () => {
    switch (status) {
      case 'green':
        return 'bg-success-600';
      case 'amber':
        return 'bg-warning-600';
      case 'red':
        return 'bg-error-600';
      default:
        return 'bg-neutral-600';
    }
  };

  const getHeight = () => {
    switch (height) {
      case 'sm':
        return 'h-2';
      case 'md':
        return 'h-3';
      case 'lg':
        return 'h-4';
    }
  };

  if (variant === 'stacked') {
    // If segments are provided, use them for rendering
    if (segments && segments.length > 0) {
      return (
        <div className="space-y-2">
          {/* Stacked Progress Bar with segments */}
          <div className={`w-full ${getHeight()} bg-neutral-200 dark:bg-neutral-800 rounded-full overflow-hidden flex`}>
            {segments.map((segment, index) => (
              <div
                key={index}
                className={`${segment.color} transition-all`}
                style={{ width: `${Math.min(segment.value, 100)}%` }}
              />
            ))}
          </div>

          {/* Legend */}
          {showLegend && (
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {segments.map((segment, index) => (
                <div key={index} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded ${segment.color}`} />
                  <span>{segment.label}: {formatPercent(segment.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Default stacked rendering without segments
    return (
      <div className="space-y-2">
        {/* Stacked Progress Bar */}
        <div className={`w-full ${getHeight()} bg-neutral-200 dark:bg-neutral-800 rounded-full overflow-hidden flex`}>
          {/* Actual */}
          <div
            className={`${getBarColor()} transition-all`}
            style={{ width: `${Math.min(actualPercent, 100)}%` }}
          />
          {/* Committed */}
          {committed > 0 && (
            <div
              className="bg-info-400 transition-all"
              style={{ width: `${Math.min(committedPercent, 100 - actualPercent)}%` }}
            />
          )}
        </div>

        {/* Labels */}
        {displayShowLabels && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className={`w-3 h-3 rounded ${getBarColor()}`} />
                <span>Actual: {formatCurrency(displayActual)}</span>
                {showPercentage && <span className="text-muted-foreground">({formatPercent(actualPercent)})</span>}
              </div>
              {committed > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-info-400" />
                  <span>Committed: {formatCurrency(committed)}</span>
                  {showPercentage && <span className="text-muted-foreground">({formatPercent(committedPercent)})</span>}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-neutral-200 dark:bg-neutral-800" />
                <span>Available: {formatCurrency(displayBudget - displayActual - committed)}</span>
                {showPercentage && <span className="text-muted-foreground">({formatPercent(availablePercent)})</span>}
              </div>
            </div>
            <span className="font-medium">{label || `Budget: ${formatCurrency(displayBudget)}`}</span>
          </div>
        )}
      </div>
    );
  }

  // Default variant - simple progress bar
  const getAccent = () => {
    switch (status) {
      case 'green':
        return 'success' as const;
      case 'amber':
        return 'warning' as const;
      case 'red':
        return 'destructive' as const;
      default:
        return undefined;
    }
  };

  return (
    <div className="space-y-2">
      <Progress
        value={totalPercent}
        className={getHeight()}
        accent={getAccent()}
      />
      {displayShowLabels && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {label || formatCurrency(displayActual)}
            {committed > 0 && ` + ${formatCurrency(committed)} committed`}
          </span>
          <span>
            {showPercentage && `${formatPercent(totalPercent)} of `}
            {formatCurrency(displayBudget)}
          </span>
        </div>
      )}
    </div>
  );
}

// Compact inline progress indicator
interface InlineProgressProps {
  percent: number;
  status: BudgetStatus;
  width?: string;
}

export function InlineProgress({ percent, status, width = 'w-24' }: InlineProgressProps) {
  const getBarColor = () => {
    switch (status) {
      case 'green':
        return 'bg-success-600';
      case 'amber':
        return 'bg-warning-600';
      case 'red':
        return 'bg-error-600';
      default:
        return 'bg-neutral-600';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`${width} h-2 bg-neutral-200 dark:bg-neutral-800 rounded-full overflow-hidden`}>
        <div
          className={`h-full ${getBarColor()} transition-all`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground min-w-[3rem] text-right">
        {formatPercent(percent)}
      </span>
    </div>
  );
}
